/**
 * avsyncTelemetry — persistent (cross-turn) A/V debug counters (phase 3.8, W6).
 *
 * The per-turn `AvsyncStats` (avatarScheduler.ts) resets on every
 * `assistant_audio_start`; these counters must survive across turns (frame-arrival
 * gaps, pick distribution, watchdog fires, mic-send skips …), so they live on a
 * single `persistent` object carried across resets. All helpers are pure so they
 * unit-test with esbuild+node; the `?avdebug=1` flag only gates console output,
 * the counters are always collected (cheap).
 */

import type { GateReason } from './sync';

// Histogram-lite bucket edges (ms). A gap `g` lands in bucket `i` where it is the
// first edge with `g < edges[i]`; values ≥ the last edge go in the overflow bucket.
export const FRAME_GAP_BUCKETS_MS = [50, 100, 200, 400, 800];
export const GATE_REASON_HISTORY_CAP = 64; // bounded per-turn gate-reason ring
// SPEC 4.11 W-B attribution rings (playback stops / ctx health / visibility) — same
// bound as the gate-reason ring; cheap, always collected, surfaced on __avsyncStats.
export const TELEMETRY_RING_CAP = 64;
// REQ-B5: only flag a content shortfall when the client scheduled MATERIALLY fewer ms
// than the server sent — one video-frame of slack absorbs clause-boundary rounding.
export const AUDIO_END_SHORT_TOLERANCE_MS = 100;
// SPEC 4.15 W1: threshold for an ANOMALOUS early source end — a scheduled TTS
// source whose onended fires this many ms before its ctx-clock end while running
// and NOT force-stopped by a tracked barge/overlap. NOTE (audit F4): this cannot
// confirm the reported "last word before a full stop" if it is a below-sink
// (OS/device) dropout — such a dropout does NOT shorten the scheduled source, so
// onended still fires at the ctx-clock end and this predicate stays quiet. An
// EMPTY clauseShortfalls ring during a heard clip is therefore itself the signal
// that the cut was below the JS layer (the deferred AnalyserNode output-tap is
// the only positive confirm of that path).
export const CLAUSE_SHORTFALL_TOLERANCE_MS = 60;

/** One recorded gate-open per turn (F3/W6): why the audio started, and when. */
export interface GateReasonSample {
  turn: number | null;
  reason: GateReason;
}

/** Why a still-scheduled TTS source run was stopped (SPEC 4.11 REQ-B2). */
export type PlaybackStopCause = 'barge' | 'overlap' | 'disconnect' | 'stop';

/**
 * One `stopScheduledSources()` invocation (REQ-B2/B3). `turn` is the epoch at stop
 * time (the epoch being guillotined — the PREV epoch for an overlap stop). A record
 * with `playedMs < scheduledContentMs` is a mid-content cut.
 */
export interface PlaybackStop {
  turn: number | null;
  cause: PlaybackStopCause;
  playedMs: number;
  scheduledContentMs: number;
  wall: number;
}

/** AudioContext health on the FIRST scheduled chunk of a turn (REQ-B4). A
 *  `ctxState !== 'running'` here is the cold-start smoking gun for W-A (REQ-A1). */
export interface CtxHealthSample {
  turn: number | null;
  ctxState: string; // AudioContextState ('running' | 'suspended' | 'closed')
  currentTime: number;
  outputLatency: number;
}

/** A `document.visibilitychange` breadcrumb (REQ-B6): tab/device suspend over the
 *  SSH tunnel is otherwise invisible. */
export interface VisibilitySample {
  state: string; // document.visibilityState
  wall: number;
}

/** Server-sent vs client-scheduled reply length at drain (REQ-B5). */
export interface AudioEndShortSample {
  turn: number | null;
  scheduledMs: number;
  serverMs: number;
}

/** REQ-B5 shortfall flag: a count plus the last mismatch. */
export interface AudioEndShort {
  count: number;
  last: AudioEndShortSample | null;
}

/**
 * SPEC 4.15 W1: one ANOMALOUS early source end. A scheduled TTS source's onended
 * fired earlier than its ctx-clock end while the context was running and no tracked
 * barge/overlap force-stopped it. This catches an early source.stop()/scheduling
 * anomaly — NOT a below-sink (OS/device) dropout, which leaves the scheduled source
 * intact and never trips this (audit F4). `turn` is the reply epoch; `clauseIdx` is
 * the 0-based chunk within it. (vs REQ-B5, which catches PCM lost before scheduling.)
 */
export interface ClauseShortfall {
  turn: number | null;
  clauseIdx: number;
  expectedMs: number;
  actualMs: number;
  shortfallMs: number;
  wall: number;
}

/** Cross-turn A/V-sync diagnostics (persists across per-turn stats resets). */
export interface AvsyncPersistent {
  frameGapMaxMs: number; // largest video-frame inter-arrival gap seen
  frameGapHisto: number[]; // length FRAME_GAP_BUCKETS_MS.length + 1 (last = overflow)
  scheduleGaps: number; // scheduleChunk real underrun gaps (audio queue drained)
  overlapStops: number; // W4.2 previous-epoch source stops on a new turn
  micSendSkips: number; // W3.5 mic chunks dropped under WS backpressure
  watchdogFires: number; // W5.3 no-frame watchdog forced reconnects
  gateReasons: GateReasonSample[]; // per-turn gate-open reason history (bounded)
  pickDist: { draw: number; hold: number; idle: number }; // present-tick decisions
  // ── SPEC 4.11 W-B attribution telemetry (make the next TTS cut self-attributing) ──
  bargeInAcks: number; // REQ-B1 server-decided barge cuts received
  playbackStops: PlaybackStop[]; // REQ-B2/B3 every stopScheduledSources() (bounded)
  ctxHealth: CtxHealthSample[]; // REQ-B4 ctx.state on each turn's first chunk (bounded)
  audioEndShort: AudioEndShort; // REQ-B5 client-scheduled ≪ server-sent (needs REQ-B7)
  visibility: VisibilitySample[]; // REQ-B6 tab/device visibility breadcrumbs (bounded)
  clauseShortfalls: ClauseShortfall[]; // SPEC 4.15 W1 per-clause tail-clips (bounded)
}

/** Fresh zeroed persistent counters. Pure. */
export function newAvsyncPersistent(): AvsyncPersistent {
  return {
    frameGapMaxMs: 0,
    frameGapHisto: new Array(FRAME_GAP_BUCKETS_MS.length + 1).fill(0),
    scheduleGaps: 0,
    overlapStops: 0,
    micSendSkips: 0,
    watchdogFires: 0,
    gateReasons: [],
    pickDist: { draw: 0, hold: 0, idle: 0 },
    bargeInAcks: 0,
    playbackStops: [],
    ctxHealth: [],
    audioEndShort: { count: 0, last: null },
    visibility: [],
    clauseShortfalls: [],
  };
}

/** First ascending edge index the value falls below, else the overflow bucket. Pure. */
export function histoBucket(value: number, edges: number[]): number {
  for (let i = 0; i < edges.length; i++) {
    if (value < edges[i]!) return i;
  }
  return edges.length;
}

/** Record a video-frame inter-arrival gap: update max + histogram in place. Pure. */
export function recordFrameGap(p: AvsyncPersistent, gapMs: number): void {
  if (gapMs > p.frameGapMaxMs) p.frameGapMaxMs = gapMs;
  p.frameGapHisto[histoBucket(gapMs, FRAME_GAP_BUCKETS_MS)]! += 1;
}

/** Append a per-turn gate-open reason, capped to GATE_REASON_HISTORY_CAP. Pure. */
export function recordGateReason(p: AvsyncPersistent, turn: number | null, reason: GateReason): void {
  p.gateReasons.push({ turn, reason });
  if (p.gateReasons.length > GATE_REASON_HISTORY_CAP) p.gateReasons.shift();
}

/** Tally one present()-tick decision. Pure. */
export function recordPick(p: AvsyncPersistent, kind: 'draw' | 'hold' | 'idle'): void {
  p.pickDist[kind] += 1;
}

// ── SPEC 4.11 W-B record helpers (all pure; bounded rings drop the oldest) ──

/** REQ-B1: count one received `barge_in_ack` (server-decided cut). Pure. */
export function recordBargeInAck(p: AvsyncPersistent): void {
  p.bargeInAcks += 1;
}

/** REQ-B2/B3: append one `stopScheduledSources()` record, capped. Pure. */
export function recordPlaybackStop(p: AvsyncPersistent, stop: PlaybackStop): void {
  p.playbackStops.push(stop);
  if (p.playbackStops.length > TELEMETRY_RING_CAP) p.playbackStops.shift();
}

/** REQ-B4: append the first-chunk AudioContext health sample, capped. Pure. */
export function recordCtxHealth(p: AvsyncPersistent, sample: CtxHealthSample): void {
  p.ctxHealth.push(sample);
  if (p.ctxHealth.length > TELEMETRY_RING_CAP) p.ctxHealth.shift();
}

/** REQ-B6: append one visibility breadcrumb, capped. Pure. */
export function recordVisibility(p: AvsyncPersistent, sample: VisibilitySample): void {
  p.visibility.push(sample);
  if (p.visibility.length > TELEMETRY_RING_CAP) p.visibility.shift();
}

/** REQ-B5: record a client-vs-server content shortfall (count + last). Pure. */
export function recordAudioEndShort(p: AvsyncPersistent, sample: AudioEndShortSample): void {
  p.audioEndShort.count += 1;
  p.audioEndShort.last = sample;
}

/** REQ-B5 predicate: the client scheduled MATERIALLY fewer ms than the server sent
 *  (PCM lost on the wire/gate, vs a below-JS sink glitch). Pure. */
export function isAudioEndShort(scheduledMs: number, serverMs: number): boolean {
  return serverMs - scheduledMs > AUDIO_END_SHORT_TOLERANCE_MS;
}

/** SPEC 4.15 W1: append one per-clause tail-clip record, capped. Pure. */
export function recordClauseShortfall(p: AvsyncPersistent, sample: ClauseShortfall): void {
  p.clauseShortfalls.push(sample);
  if (p.clauseShortfalls.length > TELEMETRY_RING_CAP) p.clauseShortfalls.shift();
}

/** SPEC 4.15 W1 predicate: the source ended MATERIALLY before its scheduled ctx-clock
 *  end (an anomalous early source end, not clause-boundary rounding — a below-sink
 *  dropout leaves the scheduled source intact and does NOT trip this). Both args are
 *  ctx seconds; positive shortfall beyond CLAUSE_SHORTFALL_TOLERANCE_MS flags. Pure. */
export function isClauseShortfall(expectedEndSec: number, ctxNowSec: number): boolean {
  return (expectedEndSec - ctxNowSec) * 1000 > CLAUSE_SHORTFALL_TOLERANCE_MS;
}
